import { Component, OnInit } from '@angular/core';
import { OtEventService } from '../ot-event.service';
import { ActivatedRoute, Router } from '@angular/router';
import { OtSessionService } from '../ot-session.service';
import { OtEventInfo, City, RegistrationPriceModifier } from 'src/app/domain/ot-event';
import { RegistrationService } from '../../shared';
import { OtCityService } from '../ot-city.service';

@Component({
  selector: 'app-ot-registration-price',
  templateUrl: './ot-registration-price.component.html',
  styleUrls: ['./ot-registration-price.component.scss']
})
export class OtRegistrationPriceComponent implements OnInit {

  public otEventInfo: OtEventInfo;
  public priceModel: any;
  public otEvent: any;
  public registration: any;

  private otEventId: number;
  private cityId: number;
  public city: City;

  public submitted = false;
  public priceChangeAlert = false;

  constructor(
      private router: Router,
      private otEventService: OtEventService,
      private otSessionService: OtSessionService,
      private registrationService: RegistrationService,
      private cityService: OtCityService,
      route: ActivatedRoute) {

    this.otEventId = Number.parseInt(route.snapshot.paramMap.get('otEventId'), 10);
    this.cityId = Number.parseInt(route.snapshot.paramMap.get('cityId'), 10);
  }

  ngOnInit() {
    this.registration = this.otSessionService.getRegistration();
    if (this.registration === null || this.registration.otEventId !== this.otEventId) {
      this.registration = {
        happeningId: this.otEventId,
        happening: {
          id: this.otEventId,
          happeningTypeCode: 'OT_EVENT'
        },
        notes: null,
        socialGroup: null,
        registrationReasons: [],
        price: null
      };
    }

    this.cityService.getById(this.cityId).then(city => {
      this.city = city;
    });

    this.otEventService.getOtEventInfoById(this.otEventId).then(otEventInfo => {
      this.otEventInfo = otEventInfo;
      this.otEvent = this.otEventInfo.otEvent;

      this.priceModel = {
        pricing: this.otEventInfo.otEvent.pricing,
        premiums: [],
        userSelectableDiscounts: [],
        registrationPriceModifiersByPriceModifierId: {},
        appliedPriceModifierIdByTypeCode: {},
        appliedPriceModifierHeightByTypeCode: {},
        calculate: () => {
          this.registrationService.calculatePrices(this.registration).then(response => {
            if (this.registration.price !== null && this.registration.price !== response.price) {
              this.priceChangeAlert = ! this.priceChangeAlert;
            }
            Object.assign(this.registration, response);

            this.priceModel.registrationPriceModifiersByPriceModifierId = {};
            this.priceModel.appliedPriceModifierIdByTypeCode = {};
            this.priceModel.appliedPriceModifierHeightByTypeCode = {};

            if (this.registration.registrationPriceModifiers) {
              for (let i = 0; i < this.registration.registrationPriceModifiers.length; i++) {
                const registrationPriceModifier = this.registration.registrationPriceModifiers[i];


                if (registrationPriceModifier.priceModifier.type.isDiscount) {
                  const discountOptions = this.priceModel.userSelectableDiscounts
                      .filter(discount => discount.priceModifierId === registrationPriceModifier.priceModifierId);
                  if (discountOptions.length > 0) {
                    discountOptions[0].applied = true;
                  }
                } else {
                  const premiumOptions = this.priceModel.premiums.filter(premium => premium
                      .priceModifierId === registrationPriceModifier.priceModifierId);
                  if (premiumOptions.length > 0) {
                    premiumOptions[0].applied = true;
                  }
                }

                this.priceModel.registrationPriceModifiersByPriceModifierId[registrationPriceModifier.priceModifierId]
                    = registrationPriceModifier;

                this.priceModel.appliedPriceModifierIdByTypeCode[registrationPriceModifier.priceModifier.typeCode]
                    = registrationPriceModifier.priceModifier.id;

                if (! this.priceModel.appliedPriceModifierHeightByTypeCode[registrationPriceModifier.priceModifier.typeCode]) {
                  this.priceModel.appliedPriceModifierHeightByTypeCode[registrationPriceModifier.priceModifier.typeCode] = 0;
                }
                this.priceModel.appliedPriceModifierHeightByTypeCode[registrationPriceModifier
                    .priceModifier.typeCode] += registrationPriceModifier.amount;
              }
            }
          });
        }
      };

      if (this.otEvent.organization.paymentTypeConfigs && this.otEvent.organization.paymentTypeConfigs.length === 1) {
        this.registration.paymentTypeConfigId = this.otEvent.organization.paymentTypeConfigs[0].id;
      }

      if (this.priceModel.pricing.priceModifiers) {
        for (let i = 0; i < this.priceModel.pricing.priceModifiers.length; i++) {
          const priceModifier = this.priceModel.pricing.priceModifiers[i];
          if (priceModifier.type.isDiscount) {
            if (priceModifier.type.userSelectable) {
              this.priceModel.userSelectableDiscounts.push(this.createRegistrationPriceModifier(priceModifier));
            }
          } else {
            this.priceModel.premiums.push(this.createRegistrationPriceModifier(priceModifier));
          }
        }
      }

      // Calculate the initial price
      this.priceModel.calculate();
    });
  }

  public togglePriceModifier(priceModifier): any {
    if (! this.registration.registrationPriceModifiers) {
      this.registration.registrationPriceModifiers = [];
    }

    // If the priceModifier is already added, do nothing
    let wasRemoved = false;
    for (let i = 0; i < this.registration.registrationPriceModifiers.length; i++) {
      if (this.registration.registrationPriceModifiers[i].priceModifierId === priceModifier.id) {
        this.registration.registrationPriceModifiers.splice(i, 1);
        wasRemoved = true;
        break;
      }
    }

    if (! wasRemoved) {
      const registrationPriceModifier = this.createRegistrationPriceModifier(priceModifier);
      this.registration.registrationPriceModifiers.push(registrationPriceModifier);

      // If there is only one social group supported, select it automatically
      if (priceModifier.typeCode === 'SocialDiscount' && priceModifier.socialDiscount.socialGroups.length === 1) {
        registrationPriceModifier.registrationSocialDiscount.socialGroupId = priceModifier.socialDiscount.socialGroups[0].id;
      }
    }
    this.priceModel.calculate();
  }

  private createRegistrationPriceModifier(priceModifier): any {
    const registrationPriceModifier = {
      priceModifierId: priceModifier.id,
      priceModifier: priceModifier,
      registrationSocialDiscount: null,
      applied: false
    };
    if (priceModifier.typeCode === 'SocialDiscount') {
      registrationPriceModifier.registrationSocialDiscount = {};
    }
    return registrationPriceModifier;
  }

  public socialGroupChanged(registrationPriceModifier: RegistrationPriceModifier): void {
    // Assign socialGroup object - it's neccessary for displaying selected social group in the following registration steps
    registrationPriceModifier.registrationSocialDiscount.socialGroup = registrationPriceModifier.priceModifier.socialDiscount.socialGroups
        .find(socialGroup => socialGroup.id === registrationPriceModifier.registrationSocialDiscount.socialGroupId);
  }

  public goToNextStep(isFormValid: boolean): boolean {
    this.submitted = true;
    if (isFormValid && this.registration.registrationPriceModifiers) {
      for (let i = 0; i < this.registration.registrationPriceModifiers.length; i++) {
        const registrationPriceModifier = this.registration.registrationPriceModifiers[i];
        if (registrationPriceModifier.priceModifier.typeCode === 'SocialDiscount') {
          isFormValid = registrationPriceModifier.registrationSocialDiscount
              && registrationPriceModifier.registrationSocialDiscount.socialGroupId;
        }
      }
    }

    if (! isFormValid) {
      return false;
    }

    this.registration.paymentTypeConfig = this.otEvent.organization.paymentTypeConfigs
        .find(ptConfig => ptConfig.id === this.registration.paymentTypeConfigId);

    this.registration.otEvent = this.otEvent;
    this.otSessionService.setRegistration(this.registration);

    this.router.navigateByUrl('/city/' + this.cityId + '/ot/event/' + this.otEventId + '/registration/person');
    return true;
  }
}
